import axios from "axios";
import { authEndpoints } from "./endpoints";

class ApiClient {
  constructor(baseURL, storage, routeOnLogout) {
    this.axiosClient = axios.create({
      baseURL,
      timeout: 3000
    });
    this.storage = storage;
    this.routeOnLogout = routeOnLogout;
  }

  async _authRequest(method, url, data, params, refresh = true) {
    const accessToken = await this.storage.getItem("accessToken");
    const headers = accessToken
      ? { Authorization: `Bearer ${accessToken}` }
      : {};

    try {
      const response = await this.axiosClient({
        method,
        url,
        data,
        params,
        headers,
      });
      return response;
    } catch (error) {
      if (error.response?.status === 401) {
        const refreshToken = await this.storage.getItem("refreshToken");
        if (refresh && refreshToken) {
          try {
            const response = await this.axiosClient.post(
              authEndpoints.refreshtoken,
              { refresh: refreshToken },
            );
            const newAccessToken = response?.data?.access;
            if (newAccessToken) {
              await this.storage.setItem("accessToken", newAccessToken);
              // Retry the original request after updating the access token
              return this._authRequest(method, url, data, params, false);
            }
          } catch (error) {
            console.log("Error refreshing token", error);
          }
        }
        this.logout().finally(() => {
          this.routeOnLogout?.();
        });
      }
      return Promise.reject(error);
    }
  }

  async get(url, params) {
    return this._authRequest("get", url, null, params);
  }

  async put(url, data, params = {}) {
    return this._authRequest("put", url, data, params);
  }

  async post(url, data, params = {}) {
    return this._authRequest("post", url, data, params);
  }

  async delete(url, params = {}) {
    return this._authRequest("delete", url, null, params);
  }

  async login(username, password) {
    const response = await this.axiosClient.post(authEndpoints.login, {
      username,
      password,
    });
    const { access, refresh } = response.data;
    await this.storage.setItem("accessToken", access);
    await this.storage.setItem("refreshToken", refresh);
    await Promise.all([this._fetchCreds(), this._fetchUser()]);
  }

  async _fetchCreds() {
    const response = await this.get(authEndpoints.creds);
    await this.storage.setItem("creds", JSON.stringify(response.data));
  }

  async _fetchUser() {
    const response = await this.get(authEndpoints.currentuser);
    await this.storage.setItem("user", JSON.stringify(response.data));
  }

  async getTokenFromStorage() {
    const token = await this.storage.getItem("accessToken");
    return token || null;
  }

  async getCredsFromStorage() {
    const creds = await this.storage.getItem("creds");
    return creds && creds !== "undefined" ? JSON.parse(creds) : {};
  }

  async getUserFromStorage() {
    const user = await this.storage.getItem("user");
    return user && user !== "undefined" ? JSON.parse(user) : null;
  }

  async logout() {
    try {
      const refreshToken = await this.storage.getItem("refreshToken");
      await this.axiosClient.post(authEndpoints.logout, {
        refresh: refreshToken,
      });
    } catch (error) {
      console.warn("Error in logout", error, error.stack);
    } finally {
      const keys = await this.storage.getAllKeys();
      const keysToRemove = keys.filter((key) => !key.startsWith("persistent"));
      await this.storage.multiRemove(keysToRemove);
    }
  }
}

export default ApiClient;
